export const pollinatorFriendlyAlgorithm = async (ctx) => {
    const {
        gardenMatrix,
        plants,
        config,
        utils: { validatePlacement, placePlant, createPlantMap, fillRemainingOpenAreas, fetchPollinatorPlants }
    } = ctx;

    if (!Array.isArray(gardenMatrix) || !Array.isArray(gardenMatrix[0])) {
        throw new Error('pollinatorFriendlyAlgorithm expects gardenMatrix to be a 2D array.');
    }

    const rows = gardenMatrix.length;
    const cols = gardenMatrix[0].length;
    const totalSoilCells = gardenMatrix.flat().filter(cell => cell === 1).length;

    let plantMap = createPlantMap(rows, cols);
    let plantsPlaced = 0;
    const warnings = [];

    // Track remaining counts with unique keys
    const plantCounts = {};
    plants.forEach((plant, index) => {
        const uniqueKey = `${plant.id}_${index}`;
        plantCounts[uniqueKey] = plant.count ?? Infinity;
    });

    // Normalize user plants with mapped properties
    const normalizedPlants = plants.map((plant, index) => ({
        ...plant,
        name: plant.plant_name || plant.name,
        image: plant.plant_image_url || plant.image,
        veggieImage: plant.plant_image_url || plant.veggieImage,
        plantingWidth: plant.planting_width || plant.plantingWidth || 1,
        plantingHeight: plant.planting_height || plant.plantingHeight || 1,
        isPollinatorFriendly: plant.pollinator_friendly || plant.isPollinatorFriendly || false,
        uniqueKey: `${plant.id}_${index}`
    }));

    // Fetch additional pollinator plants from database
    const pollinatorPlants = await fetchPollinatorPlants();

    if (!pollinatorPlants || pollinatorPlants.length === 0) {
        warnings.push('No additional pollinator plants available from database. Using only user-selected plants.');
    }

    // Normalize pollinator plants
    const normalizedPollinatorPlants = pollinatorPlants ? pollinatorPlants.map(plant => ({
        ...plant,
        name: plant.plant_name || plant.name,
        image: plant.plant_image_url || plant.image,
        veggieImage: plant.plant_image_url || plant.veggieImage,
        plantingWidth: plant.planting_width || plant.plantingWidth || 1,
        plantingHeight: plant.planting_height || plant.plantingHeight || 1,
        isPollinatorFriendly: true,
        isSupplementalPollinator: true
    })) : [];

    // Configuration for pollinator distribution
    const pollinatorRatio = config.pollinatorRatio || 0.3; // 30% of garden should be pollinator-friendly by default
    const clusterSize = config.clusterSize || 3; // Size of pollinator clusters
    const diversityFactor = config.diversityFactor || 0.7; // How much to prioritize plant diversity (0-1)
    const bloomSequence = config.bloomSequence || false; // Whether to consider bloom times for succession

    // Separate user plants by pollinator friendliness
    const userPollinatorPlants = normalizedPlants.filter(p => p.isPollinatorFriendly);
    const userNonPollinatorPlants = normalizedPlants.filter(p => !p.isPollinatorFriendly);

    // Calculate how many pollinator-friendly plants we want to place
    const targetPollinatorCells = Math.floor(totalSoilCells * pollinatorRatio);

    // Helper function to create pollinator clusters
    const createPollinatorClusters = (currentPlantMap) => {
        const clusters = [];

        // Find potential cluster centers (spaced out across the garden)
        const minClusterDistance = Math.max(6, Math.floor(Math.sqrt(totalSoilCells) / 3));

        for (let row = clusterSize; row < rows - clusterSize; row += minClusterDistance) {
            for (let col = clusterSize; col < cols - clusterSize; col += minClusterDistance) {
                if (gardenMatrix[row][col] === 1 && currentPlantMap[row][col] === null) {
                    // Add some randomness to cluster centers
                    const randomOffsetRow = Math.floor(Math.random() * (clusterSize * 2)) - clusterSize;
                    const randomOffsetCol = Math.floor(Math.random() * (clusterSize * 2)) - clusterSize;

                    const centerRow = Math.max(clusterSize, Math.min(rows - clusterSize - 1, row + randomOffsetRow));
                    const centerCol = Math.max(clusterSize, Math.min(cols - clusterSize - 1, col + randomOffsetCol));

                    clusters.push({
                        centerRow,
                        centerCol,
                        plantsInCluster: 0,
                        maxPlantsInCluster: clusterSize * clusterSize,
                        plantTypesUsed: new Set()
                    });
                }
            }
        }

        return clusters;
    };

    // Helper function to get diverse plant selection
    const selectDiversePlant = (availablePlants, usedTypes, diversityWeight) => {
        if (availablePlants.length === 0) return null;

        // Score plants based on diversity and availability
        const scoredPlants = availablePlants.map(plant => {
            let score = Math.random(); // Base randomness

            // Boost score for plants not yet used in this cluster
            if (!usedTypes.has(plant.name)) {
                score += diversityWeight;
            }

            // Slightly favor smaller plants for better clustering
            const plantArea = plant.plantingWidth * plant.plantingHeight;
            score += (1 / plantArea) * 0.1;

            return { plant, score };
        });

        // Sort by score and return the best option
        scoredPlants.sort((a, b) => b.score - a.score);
        return scoredPlants[0].plant;
    };

    // Phase 1: Create pollinator-friendly clusters
    let pollinatorCellsPlaced = 0;
    const clusters = createPollinatorClusters(plantMap);

    // Combine user pollinator plants with database pollinator plants
    const allPollinatorPlants = [...userPollinatorPlants, ...normalizedPollinatorPlants];

    for (const cluster of clusters) {
        const clusterRadius = Math.floor(clusterSize / 2);

        // Try to fill this cluster with diverse pollinator plants
        for (let r = cluster.centerRow - clusterRadius; r <= cluster.centerRow + clusterRadius; r++) {
            for (let c = cluster.centerCol - clusterRadius; c <= cluster.centerCol + clusterRadius; c++) {
                if (r >= 0 && r < rows && c >= 0 && c < cols &&
                    gardenMatrix[r][c] === 1 && plantMap[r][c] === null &&
                    cluster.plantsInCluster < cluster.maxPlantsInCluster &&
                    pollinatorCellsPlaced < targetPollinatorCells) {

                    // Select a diverse plant for this position
                    const availablePlants = allPollinatorPlants.filter(plant => {
                        if (plant.isSupplementalPollinator) return true; // Database plants have unlimited count
                        return plantCounts[plant.uniqueKey] > 0; // User plants check remaining count
                    });

                    const selectedPlant = selectDiversePlant(availablePlants, cluster.plantTypesUsed, diversityFactor);

                    if (selectedPlant && validatePlacement(selectedPlant, plantMap, gardenMatrix, c, r)) {
                        plantMap = placePlant(plantMap, selectedPlant, r, c);
                        plantsPlaced++;
                        cluster.plantsInCluster++;
                        cluster.plantTypesUsed.add(selectedPlant.name);

                        // Update counts for user plants
                        if (!selectedPlant.isSupplementalPollinator) {
                            plantCounts[selectedPlant.uniqueKey]--;
                        }

                        const plantArea = selectedPlant.plantingWidth * selectedPlant.plantingHeight;
                        pollinatorCellsPlaced += plantArea;

                        warnings.push(`Placed pollinator plant ${selectedPlant.name} in cluster at (${r}, ${c})`);
                    }
                }
            }
        }
    }

    // Phase 2: Fill spaces between clusters with user-selected non-pollinator plants
    // Sort by size (largest first) for better space utilization
    const sortedNonPollinatorPlants = userNonPollinatorPlants.sort((a, b) => {
        const areaA = a.plantingWidth * a.plantingHeight;
        const areaB = b.plantingWidth * b.plantingHeight;
        return areaB - areaA;
    });

    for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
            for (const plant of sortedNonPollinatorPlants) {
                const remaining = plantCounts[plant.uniqueKey];
                if (remaining <= 0) continue;

                if (validatePlacement(plant, plantMap, gardenMatrix, col, row)) {
                    plantMap = placePlant(plantMap, plant, row, col);
                    plantCounts[plant.uniqueKey]--;
                    plantsPlaced++;
                    break;
                }
            }
        }
    }

    // Phase 3: Fill any remaining spaces with remaining user pollinator plants
    const remainingUserPollinatorPlants = userPollinatorPlants.filter(p => plantCounts[p.uniqueKey] > 0);

    for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
            for (const plant of remainingUserPollinatorPlants) {
                const remaining = plantCounts[plant.uniqueKey];
                if (remaining <= 0) continue;

                if (validatePlacement(plant, plantMap, gardenMatrix, col, row)) {
                    plantMap = placePlant(plantMap, plant, row, col);
                    plantCounts[plant.uniqueKey]--;
                    plantsPlaced++;
                    pollinatorCellsPlaced += plant.plantingWidth * plant.plantingHeight;
                    break;
                }
            }
        }
    }

    // Phase 4: Additional pollinator plants to reach target ratio if needed
    if (config.prioritizePollinators && pollinatorCellsPlaced < targetPollinatorCells) {
        for (let row = 0; row < rows; row++) {
            for (let col = 0; col < cols; col++) {
                if (gardenMatrix[row][col] === 1 && plantMap[row][col] === null) {
                    // Randomly select a database pollinator plant
                    const randomPollinatorPlant = normalizedPollinatorPlants[
                        Math.floor(Math.random() * normalizedPollinatorPlants.length)
                        ];

                    if (randomPollinatorPlant && validatePlacement(randomPollinatorPlant, plantMap, gardenMatrix, col, row)) {
                        plantMap = placePlant(plantMap, randomPollinatorPlant, row, col);
                        plantsPlaced++;
                        pollinatorCellsPlaced += randomPollinatorPlant.plantingWidth * randomPollinatorPlant.plantingHeight;
                        warnings.push(`Added extra pollinator plant ${randomPollinatorPlant.name} at (${row}, ${col})`);
                    }
                }
            }
        }
    }

    // Collect unused plants
    const unusedPlants = normalizedPlants
        .filter(p => plantCounts[p.uniqueKey] > 0)
        .map(p => ({
            name: p.name,
            id: p.id,
            remaining: plantCounts[p.uniqueKey],
            plantingWidth: p.plantingWidth,
            plantingHeight: p.plantingHeight,
            reason: 'Space allocated for pollinator clusters or insufficient space'
        }));

    // Phase 5: Auto-fill if requested
    let filledMap = plantMap;
    if (config.autoFillEmpty) {
        try {
            filledMap = await fillRemainingOpenAreas(gardenMatrix, plantMap, config, plants);
            warnings.push('Filled remaining spaces with compatible plants');
        } catch (error) {
            warnings.push('Could not auto-fill remaining spaces');
        }
    }

    // Calculate final statistics
    const occupiedCells = filledMap.flat().filter(cell => cell !== null).length;
    const efficiency = totalSoilCells > 0 ? (occupiedCells / totalSoilCells) : 0;

    const pollinatorCount = filledMap.flat().filter(cell => cell && cell.isPollinatorFriendly).length;
    const actualPollinatorRatio = totalSoilCells > 0 ? (pollinatorCount / totalSoilCells) : 0;

    const uniquePollinatorTypes = new Set(
        filledMap.flat()
            .filter(cell => cell && cell.isPollinatorFriendly)
            .map(cell => cell.name)
    ).size;

    return {
        plantMap: filledMap,
        plantsPlaced,
        unusedPlants,
        warnings,
        efficiency,
        pollinatorAnalysis: {
            pollinatorPlantsPlaced: pollinatorCount,
            targetRatio: pollinatorRatio,
            actualRatio: actualPollinatorRatio,
            coverage: actualPollinatorRatio >= pollinatorRatio * 0.8 ? 'Excellent' :
                actualPollinatorRatio >= pollinatorRatio * 0.6 ? 'Good' : 'Needs Improvement',
            diversityScore: uniquePollinatorTypes,
            clustersCreated: clusters.length,
            averageClusterSize: clusters.length > 0 ?
                clusters.reduce((sum, cluster) => sum + cluster.plantsInCluster, 0) / clusters.length : 0
        }
    };
};